import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Download, Eye, FileDown, Image as ImageIcon, RefreshCw, ScanFace, Search, Stamp, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatTile, StatusPill } from '../cockpit/Cockpit';
import ActivityColumns from '../analytics/ActivityColumns';
import PersonSheet from '../analytics/PersonSheet';
import {
  downloadTextFile, formatDate, formatDateTime, formatRelative, initials, linkLabel, peopleToCsv,
} from '../analytics/analyticsFormat';

const ACTIVE_ZIP_STATUSES = ['pending', 'processing'];
const ZIP_STATUS_TONES = { completed: 'success', processing: 'warning', failed: 'danger', pending: 'neutral' };

const AnalyticsPanel = ({ gallery, galleryId, analytics, onRefresh, axiosConfig, backendUrl }) => {
  const [people, setPeople] = useState(null);
  const [zipJobs, setZipJobs] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // axiosConfig is rebuilt on every parent render; read it through a ref so it
  // never re-triggers the loaders below.
  const configRef = useRef(axiosConfig);
  configRef.current = axiosConfig;

  const loadPeople = useCallback(async () => {
    try {
      const { data } = await axios.get(`${backendUrl}/api/galleries/${galleryId}/people`, configRef.current);
      setPeople(data.people || []);
    } catch (error) {
      console.error('Error loading people:', error);
      toast.error('Failed to load people');
      setPeople([]);
    }
  }, [backendUrl, galleryId]);

  const loadZipJobs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${backendUrl}/api/galleries/${galleryId}/download-jobs`, configRef.current);
      setZipJobs(data || []);
    } catch (error) {
      console.error('Error loading ZIP requests:', error);
    }
  }, [backendUrl, galleryId]);

  useEffect(() => {
    loadPeople();
    loadZipJobs();
  }, [loadPeople, loadZipJobs]);

  // Only poll while a ZIP is still being built.
  const zipInFlight = zipJobs.some((job) => ACTIVE_ZIP_STATUSES.includes(job.status));
  useEffect(() => {
    if (!zipInFlight) return undefined;
    const timer = setInterval(loadZipJobs, 10000);
    return () => clearInterval(timer);
  }, [zipInFlight, loadZipJobs]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([onRefresh(), loadPeople(), loadZipJobs()]);
    setRefreshing(false);
  };

  const closePerson = useCallback(() => setSelectedEmail(null), []);

  const filteredPeople = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!people || !needle) return people || [];
    return people.filter((p) =>
      [p.name, p.email, p.domain].some((field) => field && field.toLowerCase().includes(needle)));
  }, [people, query]);

  const exportCsv = () => {
    if (!people?.length) return;
    const slug = (gallery?.name || 'gallery').replace(/[^a-z0-9]+/gi, '_');
    downloadTextFile(`${slug}_people_${new Date().toISOString().slice(0, 10)}.csv`, peopleToCsv(people));
  };

  const a = analytics || {};
  const tracking = !!a.tracking_since;
  const hasActivity = !!a.first_activity;
  const linkCounts = Object.entries(a.people_by_link || {}).sort((x, y) => y[1] - x[1]);

  return (
    <div className="h-full overflow-auto p-3 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-800 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
            Analytics
          </h2>
          <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Who opened {gallery?.name || 'this gallery'}, searched for themselves and downloaded, across every client link
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={!people?.length} className="h-9 flex-1 sm:flex-none">
            <FileDown className="w-4 h-4 mr-2" />
            Export people
          </Button>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing} className="h-9 flex-1 sm:flex-none">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Funnel */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-4 mb-4 md:mb-6">
        <StatTile
          hero
          label="Visitors"
          value={tracking ? (a.visitors || 0).toLocaleString() : '—'}
          detail={tracking
            ? `${(a.anonymous_visitors || 0).toLocaleString()} didn't give details · since ${formatDate(a.tracking_since)}`
            : 'Counted from the next link open'}
          icon={<Eye className="w-4 h-4" />}
          accent="#34D399"
        />
        <StatTile
          label="People"
          value={(a.people || 0).toLocaleString()}
          detail="Identified by name and email"
          icon={<Users className="w-4 h-4" />}
          accent="#7C3AED"
        />
        <StatTile
          label="Searched"
          value={(a.searchers || 0).toLocaleString()}
          detail={`${(a.searchers_with_matches || 0).toLocaleString()} found in photos · ${(a.searches || 0).toLocaleString()} searches · ${(a.match_emails || 0).toLocaleString()} match emails`}
          icon={<ScanFace className="w-4 h-4" />}
          accent="#F2A900"
        />
        <StatTile
          label="Downloaded"
          value={(a.downloaders || 0).toLocaleString()}
          detail={`${(a.downloads || 0).toLocaleString()} downloads · ${(a.photos_downloaded || 0).toLocaleString()} of ${(a.total_photos || 0).toLocaleString()} photos`}
          icon={<Download className="w-4 h-4" />}
          accent="#16A34A"
        />
        <StatTile
          label="Branded files"
          value={(a.branded_downloads || 0).toLocaleString()}
          detail={`${(a.original_downloads || 0).toLocaleString()} original · ${(a.zip_requests || 0).toLocaleString()} ZIP requests`}
          icon={<Stamp className="w-4 h-4" />}
          accent="#0FA3A3"
        />
      </div>

      {!hasActivity ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="font-medium text-slate-700 dark:text-slate-200">No visitor activity yet</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Link opens, face searches and downloads from any client link will appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4 md:space-y-6">
          {/* Daily activity */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Daily activity</CardTitle>
            </CardHeader>
            <CardContent>
              <ActivityColumns daily={a.daily || []} trackingSince={a.tracking_since} />
            </CardContent>
          </Card>

          {/* Most downloaded */}
          {a.top_photos?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Most downloaded</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {a.top_photos.map((photo) => (
                    <figure key={photo.photo_id} className="flex-shrink-0 w-32">
                      {photo.thumbnail_url ? (
                        <img src={photo.thumbnail_url} alt={photo.filename || ''} loading="lazy"
                          className="w-32 h-24 object-cover rounded-lg border border-slate-200 dark:border-slate-700" />
                      ) : (
                        <div className="w-32 h-24 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                          <ImageIcon className="w-5 h-5 text-slate-400" />
                        </div>
                      )}
                      <figcaption className="mt-1.5">
                        <div className="text-sm font-semibold text-slate-900 dark:text-white">
                          {photo.downloads} download{photo.downloads === 1 ? '' : 's'}
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                          {photo.people} {photo.people === 1 ? 'person' : 'people'} · {photo.filename || 'deleted photo'}
                        </div>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* People */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-base">People</CardTitle>
                  {linkCounts.length > 0 && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {linkCounts.map(([link, count]) => `${linkLabel(link)} ${count}`).join(' · ')}
                    </p>
                  )}
                </div>
                <div className="relative sm:w-64">
                  <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search name, email, company"
                    className="w-full h-9 pl-8 pr-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {people === null ? (
                <div className="py-10 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
              ) : filteredPeople.length === 0 ? (
                <p className="py-10 text-center text-sm text-slate-500">
                  {people.length ? 'Nobody matches that search.' : 'Nobody has entered their details yet.'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 border-y border-slate-200 dark:border-slate-700">
                      <tr className="text-left text-xs text-slate-500 dark:text-slate-400">
                        <th className="px-4 py-2.5 font-medium">Person</th>
                        <th className="px-4 py-2.5 font-medium hidden md:table-cell">Links</th>
                        <th className="px-4 py-2.5 font-medium">Face match</th>
                        <th className="px-4 py-2.5 font-medium">Downloads</th>
                        <th className="px-4 py-2.5 font-medium hidden sm:table-cell">Last active</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {filteredPeople.map((person) => (
                        <tr
                          key={person.email}
                          onClick={() => setSelectedEmail(person.email)}
                          onKeyDown={(e) => { if (e.key === 'Enter') setSelectedEmail(person.email); }}
                          tabIndex={0}
                          className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 focus:bg-slate-50 dark:focus:bg-slate-800/50 outline-none"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3 min-w-0">
                              {person.selfie_url ? (
                                <img src={person.selfie_url} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-200 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                                  {initials(person.name, person.email)}
                                </div>
                              )}
                              <div className="min-w-0">
                                <div className="font-medium text-slate-900 dark:text-white truncate max-w-[220px]">
                                  {person.name || person.email}
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[220px]">
                                  {person.name ? person.email : person.domain}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <div className="flex flex-wrap gap-1">
                              {person.links.map((link) => <StatusPill key={link} tone="info">{linkLabel(link)}</StatusPill>)}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {person.photos_found > 0 ? (
                              <div>
                                <div className="text-slate-900 dark:text-white">
                                  In {person.photos_found} photo{person.photos_found === 1 ? '' : 's'}
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                  {[
                                    person.match_emails ? `${person.match_emails} email${person.match_emails === 1 ? '' : 's'} sent` : null,
                                    person.subscribed ? 'emailed when new' : null,
                                  ].filter(Boolean).join(' · ') || `${person.searches} search${person.searches === 1 ? '' : 'es'}`}
                                </div>
                              </div>
                            ) : person.searches > 0 || person.subscribed ? (
                              <div>
                                <div className="text-amber-700 dark:text-amber-400">No matches yet</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                  {person.subscribed ? 'Emailed when found' : `${person.searches} search${person.searches === 1 ? '' : 'es'}`}
                                </div>
                              </div>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {person.downloads === 0 && person.zip_requests === 0 ? (
                              <span className="text-slate-400">None</span>
                            ) : (
                              <div>
                                <div className="text-slate-900 dark:text-white">
                                  {person.downloads} of {person.photos_downloaded} photo{person.photos_downloaded === 1 ? '' : 's'}
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                  {[
                                    person.branded_downloads ? `${person.branded_downloads} branded` : null,
                                    person.original_downloads ? `${person.original_downloads} original` : null,
                                    person.zip_requests ? `${person.zip_requests} ZIP` : null,
                                  ].filter(Boolean).join(' · ')}
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell text-slate-500 dark:text-slate-400 whitespace-nowrap" title={formatDateTime(person.last_seen)}>
                            {formatRelative(person.last_seen)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ZIP requests */}
          {zipJobs.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">ZIP requests</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 border-y border-slate-200 dark:border-slate-700">
                      <tr className="text-left text-xs text-slate-500 dark:text-slate-400">
                        <th className="px-4 py-2.5 font-medium">Requested by</th>
                        <th className="px-4 py-2.5 font-medium">Photos</th>
                        <th className="px-4 py-2.5 font-medium hidden sm:table-cell">Size</th>
                        <th className="px-4 py-2.5 font-medium">Status</th>
                        <th className="px-4 py-2.5 font-medium hidden sm:table-cell">Requested</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {zipJobs.map((job) => (
                        <tr key={job.id}>
                          <td className="px-4 py-3">
                            <div className="text-slate-900 dark:text-white">{job.user_name || '—'}</div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">{job.user_email}</div>
                          </td>
                          <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                            {job.photo_count}
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {job.favorites_list_name ? `♥ ${job.favorites_list_name}` : 'All photos'}
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell text-slate-500 dark:text-slate-400">
                            {job.size === 'social' ? '2048px' : 'Full size'}
                          </td>
                          <td className="px-4 py-3">
                            <StatusPill tone={ZIP_STATUS_TONES[job.status] || 'neutral'}>{job.status || 'pending'}</StatusPill>
                            {/* Lambda-built ZIPs email the link themselves and never report back. */}
                            {(job.email_sent || (job.status === 'completed' && job.processing_method !== 'lambda')) && (
                              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                {job.email_sent ? 'Link emailed' : 'Email not sent'}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell text-slate-500 dark:text-slate-400 whitespace-nowrap">
                            {formatDateTime(job.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <PersonSheet
        email={selectedEmail}
        onClose={closePerson}
        galleryId={galleryId}
        backendUrl={backendUrl}
        axiosConfig={axiosConfig}
      />
    </div>
  );
};

export default AnalyticsPanel;
